<template>
  <div>
    <!-- KPIs: all four count UNIQUE assets -->
    <v-row>
      <v-col cols="12" sm="6" :md="showPassRate ? 3 : 4">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="120" />
        <KpiCard v-else
                 :label="$t('totalAssets')"
                 :value="totals.assets"
                 :tooltip="$t('totalAssetsTooltip')" />
      </v-col>
      <v-col cols="12" sm="6" :md="showPassRate ? 3 : 4">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="120" />
        <KpiCard v-else
                 :label="$t('registerCompleteness')"
                 :value="totals.completeness"
                 :formatter="formatPercent"
                 :tooltip="$t('registerCompletenessTooltip')" />
      </v-col>
      <v-col cols="12" sm="6" :md="showPassRate ? 3 : 4">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="120" />
        <KpiCard v-else
                 :label="$t('commissionedPercent')"
                 :value="totals.commissioned"
                 :formatter="formatPercent"
                 :tooltip="$t('commissionedTooltip')" />
      </v-col>
      <v-col v-if="showPassRate" cols="12" sm="6" md="3">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="120" />
        <KpiCard v-else
                 :label="$t('avgPassRate')"
                 :value="totals.passRate"
                 :formatter="formatDecimalPercent" />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="4">
        <AssetStatusBarCard />
      </v-col>
      <v-col cols="12" md="8">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="460" />
        <DrawingLocationStackedBarChart v-else />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" :md="showRating ? 7 : 12">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="460" />
        <AssetCategoryStackedBarChart v-else />
      </v-col>
      <v-col v-if="showRating" cols="12" md="5">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="400" />
        <RatingDistributionChart v-else />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="460" />
        <AssetSystemStackedBarChart v-else />
      </v-col>
      <v-col cols="12" md="6">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="460" />
        <AssetTypeStackedBarChart v-else />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <AssetsGrid preset="overview" />
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import KpiCard from '@/components/cards/KpiCard.vue';
import AssetStatusBarCard from '@/components/cards/AssetStatusBarCard.vue';
import DrawingLocationStackedBarChart from '@/components/charts/DrawingLocationStackedBarChart.vue';
import AssetCategoryStackedBarChart from '@/components/charts/AssetCategoryStackedBarChart.vue';
import AssetSystemStackedBarChart from '@/components/charts/AssetSystemStackedBarChart.vue';
import AssetTypeStackedBarChart from '@/components/charts/AssetTypeStackedBarChart.vue';
import RatingDistributionChart from '@/components/charts/RatingDistributionChart.vue';
import AssetsGrid from '@/components/grids/AssetsGrid.vue';
import { useKpiQuery, num } from '@/composables/useKpiQuery';
import { REQUIRED_FIELDS } from '@/utils/assetFields';

const store = useStore();

const dataLoaded = computed(() => store.getters.getDataLoaded);
const hasAssetField = computed(() => store.getters.hasAssetField);
const showPassRate = computed(() => hasAssetField.value('passRate'));
const showRating = computed(() => hasAssetField.value('rating'));

const formatPercent = (value) => `${Math.round(Number(value) || 0)}%`;
const formatDecimalPercent = (value) => `${(Number(value) || 0).toFixed(1)}%`;

// Register Completeness: the share of unique assets carrying every identity
// field this project actually configured.
const completenessExpr = () => {
  const fields = REQUIRED_FIELDS.filter((field) => hasAssetField.value(field.alias));
  if (fields.length === 0) return '0';
  const predicate = fields.map((field) => `${field.alias} IS NOT NULL`).join(' AND ');
  return `COUNT(*) FILTER (WHERE ${predicate}) * 100.0 / NULLIF(COUNT(*), 0)`;
};

const { totals } = useKpiQuery(
  () => `
    SELECT
      COUNT(*) AS assets,
      ${completenessExpr()} AS completeness,
      COUNT(*) FILTER (WHERE isCommissioned) * 100.0 / NULLIF(COUNT(*), 0) AS commissioned,
      AVG(passRate) AS passRate
    FROM filteredAssets
  `,
  (row) => ({
    assets: num(row.assets),
    completeness: num(row.completeness),
    commissioned: num(row.commissioned),
    passRate: num(row.passRate),
  })
);
</script>
